import { DetailViewModel } from "@frui.ts/datascreens/dist";
import { bound } from "@frui.ts/helpers/dist";
import { Router } from "@frui.ts/screens/dist";
import type { interfaces } from "inversify";
import { computed, observable } from "mobx";
import OptionsService from "services/optionsService";
import { deleteNavigationName, Modules } from "config";
import type LicensePlate from "entities/licensePlate";
import ExemptionRepository from "repositories/exemptionRepository";
import EnumService from "services/enumService";
import EventBus, { exemptionDelete } from "services/eventBus";
import LocalizationService from "services/localizationService";
import NotificationService, { NotificationType } from "services/notificationService";
import formatCountryEnumName from "utils/formatCountryEnumName";
import CountrySelectViewModel from "viewModels/countrySelectViewModel";
import * as Sentry from "@sentry/react";
import type ExemptionChangePreviewResponse from "entities/exemptionChangePreviewResponse";
import ExemptionResponse from "entities/exemptionResponse";
import type { ExemptionCancellationRequest } from "entities/exemptionCancellationRequest";
import SecurityService from "services/securityService";
import { emptyValue } from "utils/emptyValue";

@Router.registerRoute({ route: deleteNavigationName, name: Router.Self })
export default class DeleteExemptionViewModel extends DetailViewModel<ExemptionResponse> {
  private disposers: Array<() => void> = [];
  private editExemptionPreview: ExemptionChangePreviewResponse;
  countrySelectViewModel: CountrySelectViewModel;
  @observable terminateStep = 1;
  @observable loading = false;
  @observable validUntilDate: Date | undefined;

  constructor(
    private exemption: ExemptionResponse,
    private repository: ExemptionRepository,
    private localizationService: LocalizationService,
    private eventBus: EventBus,
    private notificationService: NotificationService,
    private optionsService: OptionsService,
    private enumService: EnumService,
    private securityService: SecurityService,
    private countrySelectViewModelFactory: ReturnType<typeof CountrySelectViewModel.Factory>
  ) {
    super();
    this.navigationName = deleteNavigationName;
    this.countrySelectViewModel = this.countrySelectViewModelFactory("id");
  }

  onInitialize(): Promise<void> {
    return super.onInitialize();
  }

  protected onDeactivate(isClosing: boolean): Promise<any> | void {
    if (isClosing) {
      this.disposers.forEach(dispose => dispose());
      this.disposers = [];
    }

    return super.onDeactivate(isClosing);
  }

  protected loadDetail() {
    return this.exemption;
  }

  @computed
  get officialRecordId() {
    return this.exemption.officialRecordId ?? "";
  }

  @computed
  get exemptionResponse() {
    return this.exemption;
  }

  @computed
  get countries() {
    return this.enumService.values("countries").map(country => ({ id: country.id, label: formatCountryEnumName(country) }));
  }

  @computed
  get isDisabled() {
    return this.securityService.allowTo("callcenter.chargeauditing", "write");
  }

  @computed
  get exemptionReasonOptions() {
    return this.optionsService.enumOptions("exemption_reasons");
  }

  @bound
  getExemptionReasonId(exemptionReason: string) {
    const exemptionReasons = this.enumService.values("exemption_reasons");
    return exemptionReasons.find(x => x.exemptionReason === exemptionReason)?.id ?? emptyValue;
  }

  @bound
  getExemptionReason(id: string) {
    const exemptionReasons = this.enumService.values("notifiable_exemption_reasons");
    return exemptionReasons.find(x => x.id === id)?.exemptionReason;
  }

  @bound
  getExemptionReasonDescription(id: string) {
    const exemptionReasons = this.enumService.values("notifiable_exemption_reasons");
    return exemptionReasons.find(x => x.id === id)?.description;
  }

  @bound
  async onDelete() {
    this.loading = true;

    try {
      await this.repository.deleteItem(this.exemption.exemptionId);
      this.eventBus.publish(exemptionDelete({ exemptionId: this.exemption.exemptionId }));
      this.notificationService.add({ title: this.translate("cancelled"), type: NotificationType.SUCCESS });
    } catch (e) {
      Sentry.captureException(e);
    }

    this.loading = false;
    this.requestClose();
  }

  @bound
  onClose() {
    this.requestClose();
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.callCenter, `deleteExemption.${code}`, size);
  }

  translateGeneral(code: string, size?: number) {
    return this.localizationService.translateGeneral(code, size);
  }

  @bound
  translateCountryId(countryId: string) {
    if (!countryId) {
      return emptyValue;
    }
    const country = this.enumService.value("countries", countryId);

    if (!country) {
      return emptyValue;
    }
    return `${country.code} - ${country.name}`;
  }

  @computed
  get rejectedReasonText() {
    if (this.isDisabled) {
      return this.translate("warningBypassingForbidden");
    }
    return undefined;
  }

  translateAttribute(model: "exemption", attribute: keyof ExemptionCancellationRequest, size?: number): string;
  translateAttribute(model: "licensePlate", attribute: keyof LicensePlate, size?: number): string;
  translateAttribute(
    model: "exemption" | "licensePlate",
    attribute: keyof ExemptionCancellationRequest | keyof LicensePlate,
    size?: number
  ) {
    return this.localizationService.translateAttribute(model, attribute, size);
  }

  static Factory({ container }: interfaces.Context) {
    return (exemption: ExemptionResponse) =>
      new DeleteExemptionViewModel(
        exemption,
        container.get(ExemptionRepository),
        container.get(LocalizationService),
        container.get(EventBus),
        container.get(NotificationService),
        container.get(OptionsService),
        container.get(EnumService),
        container.get(SecurityService),
        container.get(CountrySelectViewModel.Factory)
      );
  }
}
